import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import type {
  VacationAccrualResult,
  VacationAccrualSnapshot,
} from "@ranfac/shared";
import { downloadVacationJe, fetchVacationAccrual } from "../api.js";
import { WorkpaperActions } from "../components/WorkpaperActions.js";

export function AccruedVacation() {
  const { period = "2026-01" } = useParams();
  const periodEnd = periodEndOf(period);
  const [data, setData] = useState<{
    snapshot: VacationAccrualSnapshot;
    result: VacationAccrualResult;
    source: "upload" | "fixture";
  } | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    fetchVacationAccrual(periodEnd)
      .then(setData)
      .catch((e) => setErr(String(e)));
  }, [periodEnd]);

  if (err) return <div className="banner warn">Could not load: {err}</div>;
  if (!data) return <div className="card">Loading…</div>;

  const { snapshot, result, source } = data;
  const varianceOk = Math.abs(result.varianceFromBook) < 0.01;

  async function onDownloadJe() {
    setBusy(true);
    try {
      const { filename, csv } = await downloadVacationJe(periodEnd);
      const blob = new Blob([csv], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
      setToast(`Downloaded ${filename}. Import via Library Master → GL Import.`);
    } catch (e) {
      setToast(`JE generation failed: ${e}`);
    } finally {
      setBusy(false);
    }
  }

  const cervosCount = result.lines.filter((l) => l.isCervos).length;

  return (
    <>
      <div className="card">
        <div style={{ marginBottom: 8 }}>
          <Link to={`/close/${period}`}>&larr; back to close dashboard</Link>
        </div>
        <h1>Accrued Vacation</h1>
        <div className="subtle">
          GL 2215 · period ending {snapshot.periodEnd} · {result.lines.length}{" "}
          employees ({cervosCount} Cervos-allocated) ·{" "}
          {source === "upload" ? (
            <span
              className="badge"
              style={{ background: "#dcfce7", color: "#166534" }}
            >
              uploaded file
            </span>
          ) : (
            <span className="badge warn">
              sample data — upload Employee Accruals to use live numbers
            </span>
          )}
        </div>

        <div className="meta" style={{ marginTop: 16 }}>
          <div className="item">
            <div className="label">Sage source</div>
            <div>PR_EmployeeAccrual, PR_Employee, PR_Department</div>
          </div>
          <div className="item">
            <div className="label">Calculation</div>
            <div>vacation hours × regular hourly rate, per employee</div>
          </div>
          <div className="item">
            <div className="label">Phase</div>
            <div>2 · MVP</div>
          </div>
        </div>
      </div>

      <div className="kpi-grid">
        <Kpi label="Total vacation hours" value={result.totalHours.toFixed(2)} />
        <Kpi label="Computed accrual" value={fmt(result.totalAccrual)} />
        <Kpi label="GL 2215 book balance" value={fmt(result.bookBalanceGl2215)} />
        <Kpi
          label="Variance vs. book"
          value={fmt(result.varianceFromBook)}
          variant={varianceOk ? "ok" : "warn"}
        />
      </div>

      <div className="card">
        <h2>Allocation by sub-account</h2>
        <table>
          <thead>
            <tr>
              <th>Sub-account</th>
              <th>Description</th>
              <th className="num">Accrual</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="monoish">2215-000-0</td>
              <td>Accrued vacation — standard</td>
              <td className="num">{fmt(result.standardAccrual)}</td>
            </tr>
            <tr>
              <td className="monoish">2215-000-1</td>
              <td>Accrued vacation — Cervos department</td>
              <td className="num">{fmt(result.cervosAccrual)}</td>
            </tr>
          </tbody>
        </table>
      </div>

      {result.employeesWithoutRate > 0 && (
        <div className="banner warn">
          {result.employeesWithoutRate} employee
          {result.employeesWithoutRate === 1 ? " has" : "s have"} vacation hours
          but no hourly rate — their accrual came in at $0. Check the Employee
          Accruals export for missing rate data.
        </div>
      )}

      <div className="toolbar">
        <button onClick={onDownloadJe} disabled={busy}>
          Generate JE (CSV)
        </button>
      </div>

      <WorkpaperActions reconId="accrued-vacation" period={period} />

      <div className="card">
        <h2>Employees</h2>
        <div style={{ overflowX: "auto" }}>
          <table>
            <thead>
              <tr>
                <th>Employee</th>
                <th>Department</th>
                <th className="num">Vac Hours</th>
                <th className="num">Rate</th>
                <th className="num">Accrual</th>
                <th>Allocation</th>
              </tr>
            </thead>
            <tbody>
              {result.lines.map((line, i) => (
                <tr
                  key={`${line.row.employeeName}-${i}`}
                  className={line.isCervos ? "row-has-adj" : ""}
                >
                  <td>{line.row.employeeName}</td>
                  <td className="subtle">{line.row.department}</td>
                  <td className="num">{line.row.vacationHours.toFixed(2)}</td>
                  <td className="num">{line.row.hourlyRate.toFixed(4)}</td>
                  <td className="num">{fmt(line.accrualAmount)}</td>
                  <td className="monoish">
                    {line.isCervos ? "2215-000-1" : "2215-000-0"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {toast && (
        <div className="toast" onClick={() => setToast(null)}>
          {toast}
        </div>
      )}
    </>
  );
}

function Kpi({
  label,
  value,
  variant,
}: {
  label: string;
  value: string | number;
  variant?: "ok" | "warn";
}) {
  return (
    <div className={`kpi ${variant ? "variance" : ""}`}>
      <div className="label">{label}</div>
      <div className={`value ${variant ?? ""}`}>{value}</div>
    </div>
  );
}

function fmt(n: number): string {
  return n.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function periodEndOf(period: string): string {
  const [y, m] = period.split("-");
  const year = Number(y);
  const month = Number(m);
  const last = new Date(Date.UTC(year, month, 0));
  return last.toISOString().slice(0, 10);
}
